interface MetaData {
  title?: string
  description?: string
  keywords?: string
  image?: string
  url?: string
  type?: 'website' | 'article' | 'product'
  siteName?: string
  author?: string
  publishedTime?: string
  modifiedTime?: string
  section?: string
  tags?: string[]
  price?: string
  currency?: string
  availability?: string
  breadcrumbs?: Array<{ name: string; url?: string }>
}

class MetaService {
  private defaultMeta: MetaData = {
    title: 'Το Κατάστημά μου',
    description: 'Η επιχείρησή μας - Αξιοπιστία και ποιότητα',
    siteName: 'Το Κατάστημά μου',
    type: 'website',
    image: '/favicon.png'
  }

  private schemaSettings: any = null

  // Load Schema.org settings from API
  async loadSchemaSettings() {
    try {
      const response = await fetch(`/backend/api/schema.php?action=get-schema-settings`)
      const data = await response.json()
      if (data.success) {
        this.schemaSettings = data.data
      }
    } catch (error) {
      console.error('Error loading schema settings:', error)
    }
  }

  // Update default meta with schema settings
  async initializeWithSchema() {
    await this.loadSchemaSettings()
    if (this.schemaSettings) {
      this.defaultMeta.siteName = this.schemaSettings.businessName || this.defaultMeta.siteName
      this.defaultMeta.description = this.schemaSettings.description || this.defaultMeta.description
    }
  }

  updatePageMeta(meta: MetaData) {
    // Clear existing Open Graph meta tags first to avoid conflicts
    this.clearOpenGraphTags()
    
    // Update document title (avoid duplicating site name if already in title)
    if (meta.title) {
      const siteName = meta.siteName || this.defaultMeta.siteName || ''
      const title = siteName && !meta.title.includes(siteName)
        ? `${meta.title} - ${siteName}`
        : meta.title
      document.title = title
    }

    // Update canonical URL (important for SEO)
    this.updateCanonicalUrl(meta.url || window.location.href)

    // Update or create meta tags
    this.updateMetaTag('description', meta.description || this.defaultMeta.description)
    this.updateMetaTag('keywords', meta.keywords)
    this.updateMetaTag('author', meta.author)

    // Open Graph tags
    this.updateMetaProperty('og:title', meta.title || this.defaultMeta.title)
    this.updateMetaProperty('og:description', meta.description || this.defaultMeta.description)
    
    // Ensure absolute URL for images
    const imageUrl = this.getFullImageUrl(meta.image || this.defaultMeta.image)
    this.updateMetaProperty('og:image', imageUrl)
    this.updateMetaProperty('og:image:secure_url', imageUrl)
    this.updateMetaProperty('og:image:type', 'image/jpeg')
    this.updateMetaProperty('og:image:width', '1200')
    this.updateMetaProperty('og:image:height', '630')
    this.updateMetaProperty('og:image:alt', meta.title || this.defaultMeta.title)
    
    this.updateMetaProperty('og:url', meta.url || window.location.href)
    this.updateMetaProperty('og:type', meta.type || this.defaultMeta.type)
    this.updateMetaProperty('og:site_name', meta.siteName || this.defaultMeta.siteName)
    this.updateMetaProperty('og:locale', 'el_GR')

    // Article specific
    if (meta.type === 'article') {
      this.updateMetaProperty('article:author', meta.author)
      this.updateMetaProperty('article:published_time', meta.publishedTime)
      this.updateMetaProperty('article:modified_time', meta.modifiedTime)
      this.updateMetaProperty('article:section', meta.section)
      
      if (meta.tags) {
        // Remove existing article tags
        this.removeMetaProperties('article:tag')
        // Add new tags
        meta.tags.forEach(tag => {
          this.addMetaProperty('article:tag', tag)
        })
      }
    }

    // Product specific
    if (meta.type === 'product') {
      this.updateMetaProperty('product:price:amount', meta.price)
      this.updateMetaProperty('product:price:currency', meta.currency || 'EUR')
      this.updateMetaProperty('product:availability', meta.availability || 'in stock')
    }

    // Twitter Card tags
    this.updateMetaName('twitter:card', 'summary_large_image')
    this.updateMetaName('twitter:title', meta.title || this.defaultMeta.title)
    this.updateMetaName('twitter:description', meta.description || this.defaultMeta.description)
    this.updateMetaName('twitter:image', this.getFullImageUrl(meta.image || this.defaultMeta.image))

    // Structured data (JSON-LD)
    this.updateStructuredData(meta)
    
    // Add breadcrumbs if provided
    if (meta.breadcrumbs && meta.breadcrumbs.length > 0) {
      this.addBreadcrumbs(meta.breadcrumbs)
    }
  }

  private updateMetaTag(name: string, content?: string) {
    if (!content) return

    let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement
    if (meta) {
      meta.content = content
    } else {
      meta = document.createElement('meta')
      meta.name = name
      meta.content = content
      document.head.appendChild(meta)
    }
  }

  private updateMetaProperty(property: string, content?: string) {
    if (!content) return

    let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement
    if (meta) {
      meta.content = content
    } else {
      meta = document.createElement('meta')
      meta.setAttribute('property', property)
      meta.content = content
      document.head.appendChild(meta)
    }
  }

  private updateMetaName(name: string, content?: string) {
    if (!content) return

    let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement
    if (meta) {
      meta.content = content
    } else {
      meta = document.createElement('meta')
      meta.name = name
      meta.content = content
      document.head.appendChild(meta)
    }
  }

  private updateCanonicalUrl(url: string) {
    // Remove existing canonical link
    const existing = document.querySelector('link[rel="canonical"]')
    if (existing) {
      existing.remove()
    }

    // Create new canonical link
    const link = document.createElement('link')
    link.rel = 'canonical'
    link.href = url
    document.head.appendChild(link)
  }

  private addMetaProperty(property: string, content: string) {
    const meta = document.createElement('meta')
    meta.setAttribute('property', property)
    meta.content = content
    document.head.appendChild(meta)
  }

  private removeMetaProperties(property: string) {
    const metas = document.querySelectorAll(`meta[property="${property}"]`)
    metas.forEach(meta => meta.remove())
  }

  private clearOpenGraphTags() {
    // Remove all existing Open Graph and Twitter meta tags
    const ogTags = document.querySelectorAll('meta[property^="og:"], meta[property^="article:"], meta[property^="product:"], meta[name^="twitter:"]')
    ogTags.forEach(tag => tag.remove())
  }

  private getFullImageUrl(imageUrl?: string): string {
    if (!imageUrl) return `${window.location.origin}/favicon.png`
    
    if (imageUrl.startsWith('http')) {
      return imageUrl
    }
    
    return `${window.location.origin}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`
  }



  private updateStructuredData(meta: MetaData) {
    // Remove existing structured data
    const existing = document.querySelector('#structured-data')
    if (existing) existing.remove()

    let structuredData: any

    if (meta.type === 'article') {
      structuredData = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": meta.title,
        "description": meta.description,
        "image": this.getFullImageUrl(meta.image),
        "author": {
          "@type": "Organization",
          "name": meta.author || this.defaultMeta.siteName
        },
        "publisher": {
          "@type": "Organization",
          "name": this.defaultMeta.siteName,
          "logo": {
            "@type": "ImageObject",
            "url": this.getFullImageUrl('/favicon.png')
          }
        },
        "datePublished": meta.publishedTime,
        "dateModified": meta.modifiedTime || meta.publishedTime,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": meta.url || window.location.href
        }
      }
    } else if (meta.type === 'product') {
      structuredData = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": meta.title,
        "description": meta.description,
        "image": this.getFullImageUrl(meta.image),
        "offers": {
          "@type": "Offer",
          "priceCurrency": meta.currency || "EUR",
          "price": meta.price,
          "availability": "https://schema.org/InStock",
          "url": meta.url || window.location.href
        }
      }
    } else {
      // For homepage/website - Use Schema.org settings if available
      if (this.schemaSettings) {
        structuredData = this.buildRichSchemaFromSettings(meta)
      } else {
        // Fallback to basic Organization + WebSite schema
        const organizationSchema = {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": meta.siteName || this.defaultMeta.siteName,
          "url": window.location.origin,
          "logo": this.getFullImageUrl(meta.image || this.defaultMeta.image),
          "description": meta.description || this.defaultMeta.description,
          "image": this.getFullImageUrl(meta.image || this.defaultMeta.image)
        }

        const websiteSchema = {
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": meta.siteName || this.defaultMeta.siteName,
          "url": window.location.origin,
          "description": meta.description || this.defaultMeta.description,
          "publisher": {
            "@type": "Organization",
            "name": meta.siteName || this.defaultMeta.siteName,
            "logo": {
              "@type": "ImageObject",
              "url": this.getFullImageUrl(meta.image || this.defaultMeta.image)
            }
          }
        }

        // Create combined structured data with both schemas
        structuredData = [organizationSchema, websiteSchema]
      }
    }

    const script = document.createElement('script')
    script.id = 'structured-data'
    script.type = 'application/ld+json'
    script.textContent = JSON.stringify(structuredData)
    document.head.appendChild(script)
  }

  // Method to get meta data from API for sharing
  async generateSharingUrl(type: 'article' | 'product', id: string): Promise<string> {
    try {
      const response = await fetch(`/api/meta/${type}/${id}`)
      const data = await response.json()
      
      if (data.success) {
        return data.sharing_url
      }
    } catch (error) {
      // Error generating sharing URL - fallback to current URL
    }
    
    return window.location.href
  }

  // Method to refresh Facebook's cache of the page
  refreshFacebookCache(url?: string): void {
    const targetUrl = url || window.location.href
    const facebookUrl = `https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(targetUrl)}`
    window.open(facebookUrl, '_blank')
  }

  // Method to preview how the page will look when shared
  previewSharing(): void {
    const meta = this.getCurrentMeta()
    
    // You could open a modal showing how the page will appear when shared
    alert(`
      Title: ${meta.title}
      Description: ${meta.description}
      Image: ${meta.image}
      URL: ${meta.url}
    `)
  }

  private getCurrentMeta(): MetaData {
    return {
      title: document.querySelector('meta[property="og:title"]')?.getAttribute('content') || document.title,
      description: document.querySelector('meta[property="og:description"]')?.getAttribute('content') || '',
      image: document.querySelector('meta[property="og:image"]')?.getAttribute('content') || '',
      url: document.querySelector('meta[property="og:url"]')?.getAttribute('content') || window.location.href,
      type: document.querySelector('meta[property="og:type"]')?.getAttribute('content') as any || 'website'
    }
  }

  // Add breadcrumb structured data
  addBreadcrumbs(breadcrumbs: Array<{ name: string; url?: string }>): void {
    // Remove existing breadcrumb script
    const existing = document.querySelector('#breadcrumb-data')
    if (existing) existing.remove()

    if (!breadcrumbs || breadcrumbs.length === 0) return

    const itemListElement = breadcrumbs.map((crumb, index) => {
      const item: any = {
        "@type": "ListItem",
        "position": index + 1,
        "name": crumb.name
      }
      
      // Only add item URL if it's not the last breadcrumb
      if (crumb.url && index < breadcrumbs.length - 1) {
        item.item = crumb.url
      }
      
      return item
    })

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": itemListElement
    }

    const script = document.createElement('script')
    script.id = 'breadcrumb-data'
    script.type = 'application/ld+json'
    script.textContent = JSON.stringify(breadcrumbSchema)
    document.head.appendChild(script)
  }

  // Build rich schema from Schema.org settings (for homepage)
  private buildRichSchemaFromSettings(meta: MetaData): any[] {
    const schemas = []
    const settings = this.schemaSettings
    const baseUrl = window.location.origin

    // 1. LocalBusiness/Organization Schema
    const organization: any = {
      "@context": "https://schema.org",
      "@type": settings.businessType || "LocalBusiness",
      "@id": baseUrl + "/#organization",
      "name": settings.businessName,
      "url": baseUrl,
      "logo": {
        "@type": "ImageObject",
        "url": this.getFullImageUrl(settings.logo)
      }
    }

    if (settings.description) {
      organization.description = settings.description
    }

    if (settings.telephone) {
      organization.telephone = settings.telephone
    }

    if (settings.email) {
      organization.email = settings.email
    }

    // Address
    if (settings.address && settings.address.street) {
      organization.address = {
        "@type": "PostalAddress",
        "streetAddress": settings.address.street,
        "addressLocality": settings.address.city,
        "postalCode": settings.address.postalCode,
        "addressCountry": settings.address.country || "GR"
      }
    }

    // Geo coordinates
    if (settings.geo && settings.geo.latitude) {
      organization.geo = {
        "@type": "GeoCoordinates",
        "latitude": settings.geo.latitude,
        "longitude": settings.geo.longitude
      }
    }

    // Opening hours
    if (settings.openingHours && Array.isArray(settings.openingHours) && settings.openingHours.length > 0) {
      organization.openingHoursSpecification = settings.openingHours.map((hours: any) => ({
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": hours.days,
        "opens": hours.opens,
        "closes": hours.closes
      }))
    }

    // Social media
    if (settings.socialMedia) {
      const sameAs = []
      if (settings.socialMedia.facebook) sameAs.push(settings.socialMedia.facebook)
      if (settings.socialMedia.instagram) sameAs.push(settings.socialMedia.instagram)
      if (settings.socialMedia.twitter) sameAs.push(settings.socialMedia.twitter)
      if (settings.socialMedia.linkedin) sameAs.push(settings.socialMedia.linkedin)
      
      if (sameAs.length > 0) {
        organization.sameAs = sameAs
      }
    }

    // Price range
    if (settings.priceRange) {
      organization.priceRange = settings.priceRange
    }

    schemas.push(organization)

    // 2. WebSite Schema
    const website: any = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": baseUrl + "/#website",
      "url": baseUrl,
      "name": settings.businessName,
      "publisher": {
        "@id": baseUrl + "/#organization"
      }
    }

    // Add search action if enabled
    if (settings.enableSiteSearch) {
      website.potentialAction = {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": baseUrl + "/search?q={search_term_string}"
        },
        "query-input": "required name=search_term_string"
      }
    }

    schemas.push(website)

    return schemas
  }
}

export const metaService = new MetaService()
export type { MetaData }